import type { ReactNode } from 'react';
import { Separator } from '@monksflow/monks-ui';
import clsx from 'clsx';

interface Props {
  children: ReactNode;
  className?: string;
}

/**
 * A "pill" container for related controls inside a `HoverControlsPill`. Owns
 * only the chrome (background, border, rounded, shadow) — NOT the padding.
 * Each child block owns its own padding so segments read as a divided control
 * (full-height `Separator`s between blocks). `items-stretch` lets those
 * separators span the full pill height; `overflow-hidden` clips blocks to the
 * rounded corners.
 */
export function ControlGroup({ children, className }: Props) {
  return (
    <div
      className={clsx(
        'pointer-events-auto flex items-stretch overflow-hidden rounded-md border border-border bg-card shadow-lg backdrop-blur-md',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Full-height divider between two blocks inside a `ControlGroup`. Overrides
 * the design-system `Separator`'s `h-full` (which collapses to 0 against the
 * group's auto height) with `h-auto` + `self-stretch` so the divider spans
 * the full pill height via the group's `items-stretch`.
 */
export function ControlGroupSeparator() {
  return (
    <Separator orientation="vertical" className="self-stretch data-[orientation=vertical]:h-auto" />
  );
}
